import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, LayoutDashboard, GitBranch, Share2, BookOpen,
  MessageSquare, Settings, ArrowRight, Clock, Hash, Zap,
} from 'lucide-react';
import { cn } from '../lib/utils';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  category: string;
  action: () => void;
  shortcut?: string;
}

interface CommandPaletteProps {
  onClose: () => void;
}

const CATEGORY_ORDER = ['Navigation', 'Recent', 'Actions'];

export function CommandPalette({ onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  function nav(path: string) {
    navigate(path);
    onClose();
  }

  const ALL_COMMANDS: CommandItem[] = [
    { id: 'dashboard',  label: 'Dashboard',       description: 'Engineering command center',   icon: LayoutDashboard, category: 'Navigation', action: () => nav('/dashboard'),     shortcut: 'G D' },
    { id: 'repos',      label: 'Repositories',    description: 'Repository intelligence',       icon: GitBranch,       category: 'Navigation', action: () => nav('/repositories'),  shortcut: 'G R' },
    { id: 'arch',       label: 'Architecture',    description: 'Service dependency explorer',   icon: Share2,          category: 'Navigation', action: () => nav('/architecture'),  shortcut: 'G A' },
    { id: 'knowledge',  label: 'Knowledge Base',  description: 'Engineering knowledge graph',   icon: BookOpen,        category: 'Navigation', action: () => nav('/knowledge'),     shortcut: 'G K' },
    { id: 'convos',     label: 'Conversations',   description: 'AI engineering conversations',  icon: MessageSquare,   category: 'Navigation', action: () => nav('/conversations'), shortcut: 'G C' },
    { id: 'settings',   label: 'Settings',        description: 'Platform configuration',        icon: Settings,        category: 'Navigation', action: () => nav('/settings') },
    { id: 'new-conv',   label: 'New Conversation',description: 'Start a new AI session',        icon: Zap,             category: 'Actions',    action: () => nav('/conversations?new=1') },
    { id: 'add-repo',   label: 'Add Repository',  description: 'Connect a new repository',      icon: GitBranch,       category: 'Actions',    action: () => nav('/repositories?add=1') },
  ];

  const filtered = query.trim()
    ? ALL_COMMANDS.filter(c =>
        c.label.toLowerCase().includes(query.toLowerCase()) ||
        c.description?.toLowerCase().includes(query.toLowerCase()) ||
        c.category.toLowerCase().includes(query.toLowerCase())
      )
    : ALL_COMMANDS;

  const grouped = CATEGORY_ORDER.reduce<Record<string, CommandItem[]>>((acc, cat) => {
    const items = filtered.filter(c => c.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const flatFiltered = Object.values(grouped).flat();

  useEffect(() => { setActive(0); inputRef.current?.focus(); }, [query]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, flatFiltered.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
      if (e.key === 'Enter') { e.preventDefault(); flatFiltered[active]?.action(); }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [active, flatFiltered, onClose]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  let flatIdx = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 backdrop-blur-sm" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} />

      {/* Palette */}
      <div
        className="relative w-full max-w-[560px] mx-4 rounded-2xl shadow-surface-lg overflow-hidden animate-slide-up border"
        style={{ backgroundColor: 'var(--s2)', borderColor: 'var(--b-default)' }}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b" style={{ borderColor: 'var(--b-subtle)' }}>
          <Search className="w-4 h-4 text-t3 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search commands, repositories, pages…"
            className="flex-1 bg-transparent text-sm text-t1 placeholder-t4 outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-xs px-1.5 py-0.5 rounded text-t3 transition-colors"
              style={{ backgroundColor: 'var(--s4)' }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[380px] overflow-y-auto py-2">
          {flatFiltered.length === 0 ? (
            <div className="py-12 text-center">
              <Hash className="w-8 h-8 text-t4 mx-auto mb-3" />
              <p className="text-sm text-t3">No results for "{query}"</p>
              <p className="text-xs text-t4 mt-1">Try a different search term</p>
            </div>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <div className="px-3 py-1.5 flex items-center gap-2">
                  <span className="mono-caps text-[10px] text-t4">{category}</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'var(--b-subtle)' }} />
                </div>
                {items.map(item => {
                  flatIdx++;
                  const itemIdx = flatIdx;
                  const isActive = itemIdx === active;
                  return (
                    <button
                      key={item.id}
                      data-active={isActive}
                      onClick={() => item.action()}
                      onMouseEnter={() => setActive(itemIdx)}
                      className={cn(
                        'w-[calc(100%-16px)] flex items-center gap-3 px-3 py-2.5 mx-2 rounded-lg text-left transition-colors',
                      )}
                      style={{
                        backgroundColor: isActive ? 'var(--brand-dim)' : 'transparent',
                      }}
                      onMouseLeave={e => {
                        if (itemIdx !== active) e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{ backgroundColor: isActive ? 'var(--overlay-lg)' : 'var(--s4)' }}
                      >
                        <item.icon className={cn('w-3.5 h-3.5', isActive ? 'text-t1' : 'text-t3')} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={cn('text-sm font-medium truncate', 'text-t1')}>
                          {item.label}
                        </div>
                        {item.description && (
                          <div className="text-xs text-t3 truncate">{item.description}</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {item.shortcut && (
                          <span className="text-[10px] text-t4 font-mono">{item.shortcut}</span>
                        )}
                        {isActive && <ArrowRight className="w-3.5 h-3.5 text-t1" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t flex items-center gap-4 text-[11px] text-t4"
          style={{ borderColor: 'var(--b-subtle)' }}>
          <span className="flex items-center gap-1">
            <kbd className="font-mono px-1 rounded text-[10px]"
              style={{ backgroundColor: 'var(--s4)', border: '1px solid var(--b-default)' }}>↑↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono px-1 rounded text-[10px]"
              style={{ backgroundColor: 'var(--s4)', border: '1px solid var(--b-default)' }}>↵</kbd>
            select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono px-1 rounded text-[10px]"
              style={{ backgroundColor: 'var(--s4)', border: '1px solid var(--b-default)' }}>Esc</kbd>
            close
          </span>
          <div className="ml-auto flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Command palette</span>
          </div>
        </div>
      </div>
    </div>
  );
}
